import { Empty, Flex, Image, Text } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { ApplicationSettingWebApp } from '@lighthouse/core'
import { SpaceVersion } from '@lighthouse/core'
import { spaceVersionEnum, useAtomAction, useAtomData, useSpaceQuotaContext } from '@lighthouse/shared'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { debounce } from 'throttle-debounce'

import { updateAppAtom } from '@/atoms/application/action'
import { applicationSettingAtom } from '@/atoms/application/state'
import { openSpaceGradeConfirm, SpaceGradeTag } from '@/components/SpaceGrade'

import { SettingCard } from '../SettingCard'

interface SettingWebAppProps {}
const SCxContainer = styled.div`
    width: 100%;
`

const SettingWebApp: React.FunctionComponent<SettingWebAppProps> = props => {
    const webApp = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.webApp, [])
    )
    const { quota } = useSpaceQuotaContext()

    const { run: updateAppSetting } = useAtomAction(updateAppAtom)
    const methods = useForm<ApplicationSettingWebApp>({
        mode: 'onChange',
        defaultValues: webApp
    })
    const { watch, control } = methods

    const handleSubmit = useMemo(
        () =>
            debounce(500, async (value: ApplicationSettingWebApp) => {
                const isSuccess = await updateAppSetting({
                    config: {
                        webApp: value
                    }
                })
            }),
        [updateAppSetting]
    )

    useEffect(() => {
        const { unsubscribe } = watch((value, { name, type }) => {
            handleSubmit(value as ApplicationSettingWebApp)
        })
        return unsubscribe
    }, [handleSubmit, updateAppSetting, watch])

    return (
        <SCxContainer>
            <Controller
                control={control}
                name="isPublishApp"
                render={({ field }) => (
                    <SettingCard
                        title={
                            <Flex alignItems="center" gap={8}>
                                <Text>下载为APP (PWA)</Text>
                                <SpaceGradeTag value={spaceVersionEnum.PROFESSIONAL} />
                            </Flex>
                        }
                        describe="此功能使您的应用可通过手机端网页下载为APP，或在电脑端通过网页下载为桌面应用。"
                        opened={field.value ?? false}
                        onChangeCollapse={v => {
                            if (
                                v &&
                                quota &&
                                quota?.currentVersionCode !== undefined &&
                                quota.currentVersionCode < spaceVersionEnum.PROFESSIONAL
                            ) {
                                openSpaceGradeConfirm(SpaceVersion.PROFESSIONAL)
                                return
                            }

                            field.onChange(v)
                        }}
                    >
                        <Flex justifyContent="center">
                            <Empty
                                styles={{
                                    root: {
                                        flex: 'unset!important',
                                        marginTop: 16
                                    },
                                    content: {
                                        textAlign: 'left',
                                        lineHeight: '22px'
                                    },
                                    wrapper: {
                                        flexDirection: 'row',
                                        justifyContent: 'flex-start'
                                    }
                                }}
                                icon={<Image width={266} height={144} radius={12} src={getAssetUrl('application', 'webApp_close.png')} />}
                            />
                        </Flex>
                    </SettingCard>
                )}
            />
        </SCxContainer>
    )
}

export default SettingWebApp
